const WS_URL = 'wss://3bqa7hkquf.execute-api.us-east-1.amazonaws.com/production'
const HEARTBEAT_INTERVAL = 240000 // 4 minutes
const MAX_RECONNECT_ATTEMPTS = 10
const MAX_RECONNECT_DELAY = 30000
const DEBOUNCE_DELAY = 500

export class BookingWebSocket {
  constructor ({ companyGroupId, date, onBookingChanged }) {
    this.companyGroupId = companyGroupId
    this.date = date
    this.onBookingChanged = onBookingChanged

    this.ws = null
    this.reconnectAttempts = 0
    this.reconnectDelay = 1000
    this.heartbeatInterval = null
    this.refreshTimer = null
    this._reconnectTimer = null
    this._destroyed = false
    this._boundVisibilityHandler = this._handleVisibilityChange.bind(this)
  }

  connect () {
    if (this._destroyed) return

    try {
      const params = new URLSearchParams({
        companyGroupId: this.companyGroupId,
        date: this.date
      })

      this.ws = new WebSocket(`${WS_URL}?${params.toString()}`)

      this.ws.onopen = () => {
        const wasReconnecting = this.reconnectAttempts > 0
        this.reconnectAttempts = 0
        this.reconnectDelay = 1000
        this._startHeartbeat()

        if (wasReconnecting && this.onBookingChanged) {
          this._debounceRefresh()
        }
      }

      this.ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data)
          if (message.type === 'pong') return
          this._debounceRefresh()
        } catch (e) {
          // ignore parse errors
        }
      }

      this.ws.onclose = (event) => {
        this._stopHeartbeat()
        if (event.code !== 1000) {
          this._reconnect()
        }
      }

      this.ws.onerror = () => {}

      if (!this._visibilityListenerAdded) {
        document.addEventListener('visibilitychange', this._boundVisibilityHandler)
        this._visibilityListenerAdded = true
      }
    } catch (e) {
      this._reconnect()
    }
  }

  updateDate (newDate) {
    this.date = newDate

    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.close()
      this.connect()
      return
    }

    try {
      this.ws.send(JSON.stringify({
        action: 'updateSubscription',
        companyGroupId: this.companyGroupId,
        date: newDate
      }))
    } catch (e) {
      this.close()
      this.connect()
    }
  }

  close () {
    this._stopHeartbeat()
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer)
      this._reconnectTimer = null
    }
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer)
      this.refreshTimer = null
    }
    if (this.ws) {
      this.ws.close(1000, 'Closing')
      this.ws = null
    }
  }

  destroy () {
    this._destroyed = true
    this.close()
    document.removeEventListener('visibilitychange', this._boundVisibilityHandler)
  }

  _debounceRefresh () {
    if (this.refreshTimer) clearTimeout(this.refreshTimer)
    this.refreshTimer = setTimeout(() => {
      if (this.onBookingChanged) this.onBookingChanged()
    }, DEBOUNCE_DELAY)
  }

  _reconnect () {
    if (this._destroyed) return
    if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) return

    this.reconnectAttempts++
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
      MAX_RECONNECT_DELAY
    )

    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null
      this.connect()
    }, delay)
  }

  _startHeartbeat () {
    this._stopHeartbeat()
    this.heartbeatInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        try {
          this.ws.send(JSON.stringify({ action: 'ping' }))
        } catch (e) {
          // will reconnect on close
        }
      }
    }, HEARTBEAT_INTERVAL)
  }

  _stopHeartbeat () {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
      this.heartbeatInterval = null
    }
  }

  _handleVisibilityChange () {
    if (document.hidden) return

    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.close()
      this.connect()
    } else {
      try {
        this.ws.send(JSON.stringify({ action: 'ping' }))
      } catch (e) {
        this.close()
        this.connect()
      }
    }
  }
}
